import {
  Apple,
  Beef,
  Beer,
  Cake,
  ChefHat,
  Coffee,
  Croissant,
  Fish,
  IceCreamCone,
  Leaf,
  type LucideIcon,
  Martini,
  Pizza,
  Salad,
  Soup,
  Utensils,
  UtensilsCrossed,
  Wine,
} from "lucide-react";

import type { TranslationKey } from "@/i18n";
import type { DietaryTag, MenuIconKey } from "@/lib/types";

export interface MenuIconOption {
  key: MenuIconKey;
  labelKey: TranslationKey;
  Icon: LucideIcon;
}

/**
 * The curated food & drink menu icon set offered in the editor. Each key maps
 * to a lucide glyph here (admin) and a @tabler glyph in the uploader. Keep the
 * keys in sync with wedding-upload-be/functions/src/menu.ts.
 */
export const MENU_ICON_OPTIONS: MenuIconOption[] = [
  { key: "welcome-drink", labelKey: "menuData.icons.welcome-drink", Icon: Martini },
  { key: "starter", labelKey: "menuData.icons.starter", Icon: UtensilsCrossed },
  { key: "soup", labelKey: "menuData.icons.soup", Icon: Soup },
  { key: "salad", labelKey: "menuData.icons.salad", Icon: Salad },
  { key: "bread", labelKey: "menuData.icons.bread", Icon: Croissant },
  { key: "fish", labelKey: "menuData.icons.fish", Icon: Fish },
  { key: "meat", labelKey: "menuData.icons.meat", Icon: Beef },
  { key: "pizza", labelKey: "menuData.icons.pizza", Icon: Pizza },
  { key: "vegetarian", labelKey: "menuData.icons.vegetarian", Icon: Leaf },
  { key: "dessert", labelKey: "menuData.icons.dessert", Icon: IceCreamCone },
  { key: "cake", labelKey: "menuData.icons.cake", Icon: Cake },
  { key: "fruit", labelKey: "menuData.icons.fruit", Icon: Apple },
  { key: "coffee", labelKey: "menuData.icons.coffee", Icon: Coffee },
  { key: "wine", labelKey: "menuData.icons.wine", Icon: Wine },
  { key: "beer", labelKey: "menuData.icons.beer", Icon: Beer },
  { key: "buffet", labelKey: "menuData.icons.buffet", Icon: ChefHat },
  { key: "utensils", labelKey: "menuData.icons.utensils", Icon: Utensils },
];

/** Default icon when a group has none chosen. */
export const DEFAULT_MENU_ICON: MenuIconKey = "utensils";

const ICON_BY_KEY = new Map<MenuIconKey, LucideIcon>(
  MENU_ICON_OPTIONS.map((option) => [option.key, option.Icon]),
);

/** Resolves an icon key to its lucide component, falling back to utensils. */
export function getMenuIcon(key: string): LucideIcon {
  return ICON_BY_KEY.get(key as MenuIconKey) ?? Utensils;
}

export interface DietaryTagOption {
  key: DietaryTag;
  labelKey: TranslationKey;
}

/**
 * Curated dietary tags shown as toggle chips in the item editor. Keep in sync
 * with wedding-upload-be/functions/src/menu.ts and the uploader registry.
 */
export const DIETARY_TAGS: DietaryTagOption[] = [
  { key: "vegetarian", labelKey: "menuData.dietary.vegetarian" },
  { key: "vegan", labelKey: "menuData.dietary.vegan" },
  { key: "glutenFree", labelKey: "menuData.dietary.glutenFree" },
  { key: "lactoseFree", labelKey: "menuData.dietary.lactoseFree" },
  { key: "nutFree", labelKey: "menuData.dietary.nutFree" },
  { key: "spicy", labelKey: "menuData.dietary.spicy" },
];

/** Caps mirrored from the backend validator. */
export const MENU_MAX_GROUPS = 12;
export const MENU_MAX_ITEMS = 100;
